import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Github, CheckCircle2, Clock, AlertCircle, ShieldCheck, Terminal, Layers } from "lucide-react";
import { CASE_STUDIES } from "../data/caseStudies";
import TechTag from "../components/common/TechTag";
import IconButton from "../components/common/IconButton";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = CASE_STUDIES.find((p) => p.slug === slug) || CASE_STUDIES[0];

  return (
    <article className="pt-36 sm:pt-40 pb-28 px-6 sm:px-8 lg:px-12">
      <div className="max-w-[1080px] mx-auto">
        
        {/* Navigation Breadcrumb */}
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/works"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Selected Work
          </Link>
          <span className="font-mono text-[10.5px] tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-border/80 bg-secondary/40 text-foreground/80">
            {project.status}
          </span>
        </div>

        {/* Hero Header */}
        <header className="mb-14">
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-primary mb-3">
            Case Study · {project.type}
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-6xl md:text-7xl font-medium tracking-display leading-[1.05]"
          >
            {project.name}
          </motion.h1>
          <p className="mt-4 text-xl sm:text-2xl text-foreground/80 font-normal leading-snug">
            {project.tagline}
          </p>

          {/* Metadata Grid */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 rounded-xl border border-border/70 bg-card/40 backdrop-blur-sm">
            <div>
              <span className="block font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground mb-1">
                Role
              </span>
              <span className="text-[13.5px] font-medium text-foreground">{project.role}</span>
            </div>
            <div>
              <span className="block font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground mb-1">
                Timeline
              </span>
              <span className="text-[13.5px] font-medium text-foreground">{project.timeline}</span>
            </div>
            <div>
              <span className="block font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground mb-1">
                Evidence Status
              </span>
              <span className="text-[13.5px] font-medium text-primary">{project.verificationStatus}</span>
            </div>
            <div>
              <span className="block font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground mb-1">
                Repositories
              </span>
              <div className="flex items-center gap-2 mt-0.5">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-[11px] tracking-wider uppercase text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> Live
                  </a>
                )}
                {project.repoUrl && (
                  <a
                    href={project.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-[11px] tracking-wider uppercase text-foreground/80 hover:text-foreground hover:underline"
                  >
                    <Github className="h-3 w-3" /> Source
                  </a>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Overview & Problem */}
        <section className="mb-14 space-y-6">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Overview & Problem Space</h2>
          <p className="text-[16.5px] text-foreground/85 leading-relaxed">{project.overview}</p>
          <div className="p-6 rounded-xl border border-border/60 bg-secondary/20">
            <h3 className="font-mono text-[11px] tracking-[0.18em] uppercase text-primary mb-2 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> The Problem
            </h3>
            <p className="text-[15px] text-foreground/80 leading-relaxed">{project.problem}</p>
          </div>
        </section>

        {/* Goals & Key Constraints */}
        <section className="mb-14">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mb-6">Goals & Specifications</h2>
          <ul className="grid sm:grid-cols-3 gap-4">
            {project.goals.map((g, idx) => (
              <li key={idx} className="p-5 rounded-xl border border-border/70 bg-card/30 flex flex-col justify-between">
                <span className="font-mono text-[10px] text-muted-foreground mb-3">0{idx + 1} / GOAL</span>
                <p className="text-[14.5px] text-foreground/85 leading-relaxed">{g}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* System Architecture */}
        <section className="mb-14">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mb-4 flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" /> System Architecture & Execution Path
          </h2>
          <p className="text-[14.5px] text-muted-foreground mb-4">
            Flow model derived directly from the source repository pipeline:
          </p>
          <div className="p-6 rounded-xl border border-border/70 bg-[#060609] overflow-x-auto font-mono text-[13px] text-primary/90 leading-relaxed">
            <pre className="whitespace-pre">{project.architecture}</pre>
          </div>
        </section>

        {/* Technical Decisions & Trade-offs */}
        <section className="mb-14">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mb-6">Technical Decisions & Trade-offs</h2>
          <div className="space-y-4">
            {project.decisions.map((d, idx) => (
              <div key={idx} className="p-5 rounded-xl border border-border/70 bg-card/40">
                <h3 className="text-lg font-medium text-foreground mb-1.5">{d.topic}</h3>
                <p className="text-[14.5px] text-primary/95 mb-2"><strong className="font-medium text-foreground">Decision:</strong> {d.decision}</p>
                <p className="text-[14px] text-muted-foreground"><strong className="font-medium text-foreground/80">Trade-off:</strong> {d.tradeoff}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Implementation Status of Features */}
        <section className="mb-14">
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mb-6">Features & Implementation Status</h2>
          <div className="divide-y divide-border/60 border-y border-border/60">
            {project.features.map((f, idx) => (
              <div key={idx} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-[15px] font-medium text-foreground">{f.name}</h4>
                  <p className="text-[13.5px] text-muted-foreground mt-0.5">{f.desc}</p>
                </div>
                <span
                  className={`self-start sm:self-center font-mono text-[10px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border ${
                    f.status === "Implemented"
                      ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                      : f.status === "In-Progress"
                      ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                      : "border-border text-muted-foreground bg-secondary/30"
                  }`}
                >
                  {f.status}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Security, Accessibility & Limitations */}
        <section className="grid sm:grid-cols-2 gap-6 mb-16">
          <div className="p-6 rounded-xl border border-border/70 bg-card/40">
            <h3 className="text-lg font-medium text-foreground mb-2 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-400" /> Security & Accessibility
            </h3>
            <p className="text-[14px] text-foreground/80 leading-relaxed">{project.securityAccessibility}</p>
          </div>
          <div className="p-6 rounded-xl border border-border/70 bg-card/40">
            <h3 className="text-lg font-medium text-foreground mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" /> Current Limitations & Roadmap
            </h3>
            <p className="text-[14px] text-foreground/80 leading-relaxed">{project.limitations}</p>
          </div>
        </section>

        {/* Footer Actions */}
        <footer className="pt-8 border-t border-border/60 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/works"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground"
          >
            &larr; Back to all works
          </Link>
          <div className="flex items-center gap-3">
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-primary text-primary-foreground font-mono text-[11px] tracking-[0.18em] uppercase hover:bg-primary/90 transition-colors"
              >
                Launch Demo <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {project.repoUrl && (
              <a
                href={project.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-border/80 bg-secondary/40 font-mono text-[11px] tracking-[0.18em] uppercase hover:bg-secondary/70 transition-colors"
              >
                GitHub Repo <Github className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </footer>

      </div>
    </article>
  );
}
